export type RecorteMode = 'live' | 'completed' | 'completed_and_open';
export type RecorteDateAnchor = 'insercao' | 'queue_entry' | 'queue_exit';
export type RecorteScope = 'both' | 'kpis_only' | 'table_only';
export type RecortePeriodPreset = '7' | '15' | '30' | '90' | 'custom' | 'all';

export type FilasVtmeRecorteState = {
  mode: RecorteMode;
  dateAnchor: RecorteDateAnchor;
  periodPreset: RecortePeriodPreset;
  fromDate: string; // yyyy-mm-dd
  toDate: string;
  scope: RecorteScope;
};

export const DEFAULT_RECORTE: FilasVtmeRecorteState = {
  mode: 'live',
  dateAnchor: 'queue_exit',
  periodPreset: '30',
  fromDate: '',
  toDate: '',
  scope: 'both',
};

export const RECORTE_STORAGE_KEY = 'cqv-filas-vtme-recorte-v1';

function storageKeyForUser(userId?: string | null): string {
  const id = (userId || '').trim();
  return id ? `${RECORTE_STORAGE_KEY}:${id}` : RECORTE_STORAGE_KEY;
}

export function normalizeRecorteState(
  partial: Partial<FilasVtmeRecorteState> | null | undefined
): FilasVtmeRecorteState {
  return { ...DEFAULT_RECORTE, ...(partial || {}) };
}

export function loadRecorteState(userId?: string | null): FilasVtmeRecorteState {
  try {
    const key = storageKeyForUser(userId);
    let raw = localStorage.getItem(key);
    // Migra chave legada global → por usuário (uma vez).
    if (!raw && userId) {
      raw = localStorage.getItem(RECORTE_STORAGE_KEY);
      if (raw) {
        localStorage.setItem(key, raw);
      }
    }
    if (!raw) return { ...DEFAULT_RECORTE };
    const parsed = JSON.parse(raw) as Partial<FilasVtmeRecorteState>;
    return normalizeRecorteState(parsed);
  } catch {
    return { ...DEFAULT_RECORTE };
  }
}

export function saveRecorteState(state: FilasVtmeRecorteState, userId?: string | null) {
  try {
    localStorage.setItem(storageKeyForUser(userId), JSON.stringify(state));
  } catch {
    // ignore quota / private mode
  }
}

export function recorteStatesEqual(a: FilasVtmeRecorteState, b: FilasVtmeRecorteState): boolean {
  return (
    a.mode === b.mode &&
    a.dateAnchor === b.dateAnchor &&
    a.periodPreset === b.periodPreset &&
    a.fromDate === b.fromDate &&
    a.toDate === b.toDate &&
    a.scope === b.scope
  );
}

export const MODE_OPTIONS: { value: RecorteMode; text: string; help: string }[] = [
  {
    value: 'live',
    text: 'Fila agora',
    help: 'Só pedidos que estão agora nos status da fila. Ideal para operação do dia (pressão, quem está fora do SLA).',
  },
  {
    value: 'completed',
    text: 'Ciclos concluídos',
    help: 'Só passagens que já saíram da fila. Ideal para “quanto tempo levou” — inclui quem já foi para status final ou outro.',
  },
  {
    value: 'completed_and_open',
    text: 'Ciclos + em andamento',
    help: 'Passagens concluídas no período e também quem ainda está na fila. Mistura desempenho histórico com o que ainda não terminou.',
  },
];

export const ANCHOR_OPTIONS: { value: RecorteDateAnchor; text: string; help: string }[] = [
  {
    value: 'insercao',
    text: 'Inserção no VTME',
    help: 'Filtra pela data em que o pedido entrou no VTME (coorte de criação), não pela passagem na fila.',
  },
  {
    value: 'queue_entry',
    text: 'Entrada na fila',
    help: 'Filtra pelo momento em que o pedido entrou nesta fila (fora → dentro).',
  },
  {
    value: 'queue_exit',
    text: 'Saída da fila',
    help: 'Filtra pelo momento em que o pedido saiu da fila. Melhor âncora para medir tempo típico de atravessar a fila.',
  },
];

export const SCOPE_OPTIONS: { value: RecorteScope; text: string; help: string }[] = [
  {
    value: 'both',
    text: 'KPIs + tabela',
    help: 'Cards (medianas/contagens) e tabela usam o mesmo universo. Recomendado no dia a dia — números e lista batem.',
  },
  {
    value: 'kpis_only',
    text: 'Só KPIs',
    help: 'O recorte (modo + período) vale só nos cards. A tabela mostra a fila ao vivo (pedidos ainda nos status da fila), independente do período.',
  },
  {
    value: 'table_only',
    text: 'Só tabela',
    help: 'O recorte vale só na lista. Os KPIs dos cards usam a fila ao vivo (pedidos ainda na fila), independentemente do período.',
  },
];

export const PERIOD_OPTIONS: { value: RecortePeriodPreset; text: string; help: string }[] = [
  {
    value: '7',
    text: '7 dias',
    help: 'Últimos 7 dias corridos até agora (fim do dia de hoje).',
  },
  {
    value: '15',
    text: '15 dias',
    help: 'Últimos 15 dias corridos até agora.',
  },
  {
    value: '30',
    text: '30 dias',
    help: 'Últimos 30 dias — padrão para “como foi o mês recente”.',
  },
  {
    value: '90',
    text: '90 dias',
    help: 'Últimos 90 dias — visão trimestral.',
  },
  {
    value: 'custom',
    text: 'Personalizado',
    help: 'Escolha data inicial e final (dias civis no fuso do navegador).',
  },
  {
    value: 'all',
    text: 'Todos',
    help: 'Sem janela de data. Em “Fila agora” mostra todos os vivos; em ciclos, todo o histórico disponível nos pedidos carregados.',
  },
];

/** Exemplos curtos por modo — usados no painel “Entenda o recorte”. */
export const RECORTE_EXAMPLES: Record<RecorteMode, string[]> = {
  live: [
    '“Quem está na fila agora e fora do SLA?” → Modo Fila agora + período Todos + escopo KPIs + tabela.',
    '“Dos pedidos inseridos esta semana, quem ainda está na fila?” → Fila agora + 7 dias (filtra por inserção).',
  ],
  completed: [
    '“Quanto tempo levou atravessar a fila nos últimos 30 dias?” → Ciclos concluídos + âncora Saída da fila + 30 dias.',
    '“Pedidos criados em março que já saíram da fila” → Ciclos concluídos + âncora Inserção + período personalizado.',
  ],
  completed_and_open: [
    '“Tudo que passou ou ainda está na fila no mês” → Ciclos + em andamento + âncora Entrada ou Saída + 30 dias.',
    'Útil para não “sumir” com quem ainda não terminou o ciclo ao olhar o histórico.',
  ],
};

/** Resolve intervalo UTC [from, to] inclusivo por dia civil local. */
export function resolveRecorteRange(
  state: FilasVtmeRecorteState,
  now = new Date()
): { fromUtc: Date | null; toUtc: Date | null; diasInseridos: number | null } {
  if (state.mode === 'live' && state.periodPreset === 'all') {
    return { fromUtc: null, toUtc: null, diasInseridos: null };
  }

  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  if (state.periodPreset === 'custom') {
    if (!state.fromDate && !state.toDate) return { fromUtc: null, toUtc: null, diasInseridos: null };
    const from = state.fromDate ? parseLocalDateStart(state.fromDate) : null;
    const to = state.toDate ? parseLocalDateEnd(state.toDate) : end;
    const dias =
      from && to ? Math.max(1, Math.ceil((to.getTime() - from.getTime()) / 86400000)) : null;
    return { fromUtc: from, toUtc: to, diasInseridos: dias };
  }

  if (state.periodPreset === 'all') {
    return { fromUtc: null, toUtc: null, diasInseridos: null };
  }

  const days = Number.parseInt(state.periodPreset, 10);
  const from = new Date(end);
  from.setDate(from.getDate() - (Number.isFinite(days) ? days : 30));
  from.setHours(0, 0, 0, 0);
  return {
    fromUtc: from,
    toUtc: end,
    diasInseridos: Number.isFinite(days) ? days : 30,
  };
}

function parseLocalDateStart(isoDate: string): Date {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
}

function parseLocalDateEnd(isoDate: string): Date {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1, 23, 59, 59, 999);
}

export function isDateInRange(iso: string | null | undefined, from: Date | null, to: Date | null): boolean {
  if (!from && !to) return true;
  if (!iso) return false;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  if (from && t < from.getTime()) return false;
  if (to && t > to.getTime()) return false;
  return true;
}

export function recorteSummary(state: FilasVtmeRecorteState): string {
  const mode = MODE_OPTIONS.find(o => o.value === state.mode)?.text ?? state.mode;
  const scope = SCOPE_OPTIONS.find(o => o.value === state.scope)?.text ?? state.scope;
  if (state.mode === 'live') {
    const period =
      state.periodPreset === 'all'
        ? 'sem recorte de data'
        : state.periodPreset === 'custom'
          ? `inserção ${state.fromDate || '…'} → ${state.toDate || '…'}`
          : `últimos ${state.periodPreset} dias (inserção)`;
    return `${mode} · ${period} · ${scope}`;
  }
  const anchor = ANCHOR_OPTIONS.find(o => o.value === state.dateAnchor)?.text ?? state.dateAnchor;
  const period =
    state.periodPreset === 'all'
      ? 'todo o histórico'
      : state.periodPreset === 'custom'
        ? `${state.fromDate || '…'} → ${state.toDate || '…'}`
        : `últimos ${state.periodPreset} dias`;
  return `${mode} · ${anchor} · ${period} · ${scope}`;
}
